/**
 * radarUnits.ts — تحويل انعكاسية الرادار إلى معدّل مطر، ووصفه بالكلام.
 *
 * ── لماذا لا يكفي dBZ ─────────────────────────────────────────────────────────
 *
 * ‏dBZ وحدةٌ فيزيائية صحيحة لكنها **لا تعني شيئاً لغير المختصّ**: «45 dBZ» لا تقول
 * للقارئ هل يخرج بمظلّة أم لا. لذلك تعرض Windy المعدّل بـmm/h وتعرض Zoom Earth
 * وصفاً بالكلمات («أمطار خفيفة»/«غزيرة»). ونُبقي dBZ خياراً لمن يريدها.
 *
 * ── علاقة التحويل، وحدودها ────────────────────────────────────────────────────
 *
 * العلاقة القياسية هي **مارشال-پالمر**: ‏Z = 200·R^1.6، ومنها R = (Z/200)^(1/1.6)
 * حيث Z = 10^(dBZ/10) بوحدة mm⁶/m³ و R بـmm/h.
 *
 * وهي **تقدير لا قياس**، ويجب ألّا يُنسى ذلك: الرادار يقيس انعكاسية القطرات لا
 * كمّية الماء، والعلاقة بينهما تتغيّر بحجم القطرة ونوع الهطول. البَرَد يعطي
 * انعكاسية هائلة بمطرٍ قليل، والثلج يعطي انعكاسية ضعيفة. فالرقم صالح للمطر
 * السائل، ومضلّل فوق ~55 dBZ حيث يغلب البَرَد — ولذلك يُقصّ هناك.
 *
 * وهذا بالضبط ما يفعله كل تطبيق يعرض mm/h من رادار؛ الفرق أن نقولها.
 */

export type RadarUnit = 'dbz' | 'mmh';

/** معامِلا مارشال-پالمر القياسيان للمطر السائل. */
const MP_A = 200;
const MP_B = 1.6;

/** فوقها يغلب البَرَد فيصير التحويل مضلّلاً — يُقصّ الناتج ولا يُستكمَل. */
export const MMH_RELIABLE_MAX_DBZ = 55;

/** يحوّل dBZ إلى mm/h بعلاقة مارشال-پالمر. */
export function dbzToMmH(dbz: number): number {
    const capped = Math.min(dbz, MMH_RELIABLE_MAX_DBZ);
    return Math.pow(Math.pow(10, capped / 10) / MP_A, 1 / MP_B);
}

/**
 * صياغة الرقم بدقّة تتناسب مع مقداره.
 *
 * ‏0.12 mm/h و 0.1 mm/h فرقٌ يعني شيئاً عند الرذاذ ولا يعني شيئاً عند 47 mm/h.
 * وعرضُ كسرين دائماً يُوهم بدقّة لا يملكها تقديرٌ من انعكاسية.
 */
export function formatMmH(mmh: number): string {
    if (mmh < 0.1) return '<0.1';
    if (mmh < 1) return mmh.toFixed(2);
    if (mmh < 10) return mmh.toFixed(1);
    return Math.round(mmh).toString();
}

/**
 * عتبات وصف الشدّة — بـmm/h لا بـdBZ.
 *
 * الحدود من اصطلاح الأرصاد الشائع للمطر السائل: خفيف دون 2.5، معتدل حتى 10،
 * غزير حتى 50، وغزير جدّاً فوقها. وربطُها بـmm/h لا بـdBZ مقصود: هي عتبات
 * **كمّية ماءٍ** أصلاً، فترجمتها إلى dBZ تُدخل تقريب مارشال-پالمر مرّتين.
 */
const INTENSITY: { max: number; key: string }[] = [
    { max: 0.25, key: 'radar.intensity.trace' },
    { max: 2.5, key: 'radar.intensity.light' },
    { max: 10, key: 'radar.intensity.moderate' },
    { max: 50, key: 'radar.intensity.heavy' },
    { max: Infinity, key: 'radar.intensity.violent' },
];

/** مفتاح ترجمة وصف الشدّة من dBZ. */
export function intensityKey(dbz: number): string {
    // البَرَد يُوصف بنفسه لا بمعدّل مطرٍ مشتقّ منه: 60 dBZ تعني بَرَداً غالباً،
    // وترجمتها إلى «أمطار غزيرة جدّاً» تُخفي الخطر الحقيقي خلف وصفٍ مألوف.
    if (dbz >= 60) return 'radar.intensity.hail';
    const mmh = dbzToMmH(dbz);
    return (INTENSITY.find((r) => mmh < r.max) ?? INTENSITY[INTENSITY.length - 1]).key;
}
